import {
  AcademicCapIcon,
  ArrowsRightLeftIcon,
  BanknotesIcon,
  BriefcaseIcon,
  BuildingOffice2Icon,
  BuildingOfficeIcon,
  ChatBubbleLeftRightIcon,
  CreditCardIcon,
  CurrencyDollarIcon,
  GlobeAltIcon,
  HeartIcon,
  HomeModernIcon,
  InformationCircleIcon,
  PaperAirplaneIcon,
  QuestionMarkCircleIcon,
  ReceiptPercentIcon,
  RectangleGroupIcon,
  SparklesIcon,
  Squares2X2Icon,
  TruckIcon,
  TvIcon,
  UserCircleIcon,
  UserGroupIcon,
  UsersIcon,
  WrenchScrewdriverIcon,
} from '@heroicons/react/24/outline';

export type MenuIcon = React.ComponentType<React.SVGProps<SVGSVGElement>>;

export interface MegaLink {
  name: string;
  href: string;
  icon: MenuIcon;
}

export interface MegaGroup {
  heading: string;
  links: MegaLink[];
}

export interface MegaPromo {
  eyebrow?: string;
  title: string;
  text: string;
  bullets?: string[];
  ctaLabel: string;
  ctaHref: string;
}

export interface MegaIntro {
  title: string;
  text: string;
  ctaLabel: string;
  ctaHref: string;
}

export interface MegaPanel {
  /** Stable id used for aria-controls and open-state tracking */
  id: string;
  label: string;
  /** Optional landing page for the whole section */
  href?: string;
  /** Line shown in the panel's closing action strip */
  footerNote: string;
  intro?: MegaIntro;
  /** Each entry is one visual column, holding one or more grouped lists */
  columns: MegaGroup[][];
  promo?: MegaPromo;
}

type Translate = (key: string) => string;

/**
 * Desktop mega-menu + mobile accordion content.
 * Every href here already existed in the previous nav — this only regroups
 * them into panels instead of five levels of hover-cascading submenus.
 */
export const getMegaPanels = (t: Translate): MegaPanel[] => [
  {
    id: 'about',
    label: t('nav.about'),
    href: '/who-we-are',
    footerNote: 'Have a question about MyBank? Our team is a call away.',
    intro: {
      title: t('nav.about'),
      text: 'Learn about MyBank’s story, leadership and the values behind our banking services in Somalia.',
      ctaLabel: 'Learn more about us',
      ctaHref: '/who-we-are',
    },
    columns: [
      [
        {
          heading: 'The Bank',
          links: [
            { name: t('nav.whoWeAre'), href: '/who-we-are', icon: InformationCircleIcon },
            { name: t('nav.whatDrivesUs'), href: '/what-drives-us', icon: SparklesIcon },
            { name: t('nav.ourBoardOfDirectors'), href: '/board-of-directors', icon: UserGroupIcon },
          ],
        },
      ],
      [
        {
          heading: 'Resources',
          links: [
            { name: t('nav.mybankFaqs'), href: '/faqs', icon: QuestionMarkCircleIcon },
            { name: 'Download Center', href: '/download-center', icon: RectangleGroupIcon },
            { name: t('nav.news'), href: '/news', icon: ChatBubbleLeftRightIcon },
          ],
        },
      ],
    ],
    promo: {
      eyebrow: 'Careers',
      title: 'Work at MyBank',
      text: 'See where you could fit in our growing team.',
      ctaLabel: 'View openings',
      ctaHref: '/careers',
    },
  },
  {
    id: 'banking',
    label: t('nav.bankingSolutions'),
    footerNote: 'Not sure which account fits you? We’ll help you choose.',
    columns: [
      [
        {
          heading: 'Personal Banking',
          links: [
            { name: t('nav.personalCurrentAccount'), href: '/personal-current-account', icon: UserCircleIcon },
            { name: t('nav.vipCurrentAccount'), href: '/vip-current-account', icon: SparklesIcon },
            { name: t('nav.jointAccount'), href: '/joint-account', icon: UsersIcon },
            { name: t('nav.studentCurrentAccount'), href: '/student-current-account', icon: AcademicCapIcon },
            { name: t('nav.savingsAccount'), href: '/savings-account', icon: BanknotesIcon },
            { name: t('nav.masterCardDebitCard'), href: '/debit-card', icon: CreditCardIcon },
          ],
        },
      ],
      [
        {
          heading: 'Personal Financing',
          links: [
            { name: t('nav.motorVehicleFinancing'), href: '/vehicle-financing', icon: TruckIcon },
            { name: t('nav.homeFinancing'), href: '/home-financing', icon: HomeModernIcon },
            { name: t('nav.electronicsFinancing'), href: '/electronics-financing', icon: TvIcon },
            { name: t('nav.furnitureFinancing'), href: '/furniture-financing', icon: RectangleGroupIcon },
            { name: t('nav.microfinance'), href: '/microfinance', icon: CurrencyDollarIcon },
          ],
        },
      ],
      [
        {
          heading: 'Business Banking',
          links: [
            { name: t('nav.soleProprietor'), href: '/sole-proprietor-account', icon: BriefcaseIcon },
            { name: t('nav.smeCorporationAccount'), href: '/sme-corporation', icon: BuildingOffice2Icon },
            { name: t('nav.billerAccount'), href: '/biller-account', icon: ReceiptPercentIcon },
            { name: t('nav.schoolsAccount'), href: '/education-account', icon: AcademicCapIcon },
            { name: t('nav.ngosAccount'), href: '/ngo-account', icon: HeartIcon },
            { name: t('nav.societyAccount'), href: '/society-account', icon: UserGroupIcon },
            { name: t('nav.otherBusinessTypesAccount'), href: '/other-business-types', icon: Squares2X2Icon },
          ],
        },
      ],
      [
        {
          heading: 'Business Financing',
          links: [
            { name: t('nav.constructionFinancing'), href: '/construction-financing', icon: WrenchScrewdriverIcon },
            { name: t('nav.tradeFinancing'), href: '/trade-financing', icon: ArrowsRightLeftIcon },
            { name: t('nav.propertyFinancing'), href: '/property-financing', icon: BuildingOfficeIcon },
          ],
        },
        {
          heading: 'Diaspora Banking',
          links: [
            { name: 'Diaspora Banking', href: '/diaspora-banking', icon: GlobeAltIcon },
            { name: t('nav.moneyTransfers'), href: '/diaspora-banking#money-transfers', icon: PaperAirplaneIcon },
            { name: t('nav.diasporaHomes'), href: '/diaspora-banking#diaspora-homes', icon: HomeModernIcon },
          ],
        },
      ],
    ],
    promo: {
      eyebrow: 'New customers',
      title: 'Open an account with MyBank',
      text: 'Start banking with the cards, transfers and payment tools built for Somalia.',
      bullets: ['Mastercard debit card', 'Transfers across borders', 'Support in branch or by phone'],
      ctaLabel: 'Open an account',
      ctaHref: '/open-account',
    },
  },
];

/** Simple top-level links that have no panel */
export const getSimpleLinks = (t: Translate) => [
  { name: t('nav.services'), href: '/services' },
  { name: t('nav.contact'), href: '/contact-us' },
  { name: t('nav.news'), href: '/news' },
  { name: 'Gallery', href: '/gallery' },
];
